/**
 * HOF-696 D18 — the unsent "+ Tätigkeit" form, kept on the phone.
 *
 * The pilot is online only (A5): a field worker loses signal, and the entry they
 * just tapped together must not be lost with it. The form — and the `client_id`
 * minted for it — is written to localStorage on every change and cleared only
 * when the server accepted the PUT. Because the id survives, a retry after a
 * network error or a reload is the SAME idempotent PUT (D8, D13), never a
 * duplicate entry.
 *
 * Scoped per farm and user, so two people sharing a phone never see each other's
 * draft. Every storage access is guarded: private mode and blocked site data
 * just mean no draft survives.
 */

export const DURATION_MIN = 15;
export const DURATION_MAX = 720;
export const DURATION_STEP = 15;
export const DEFAULT_DURATION = 120;

export type DraftStep = 0 | 1 | 2;

export interface LabourDraft {
  clientId: string;
  workDay: string;
  step: DraftStep;
  activityKey: string | null;
  costObjectKey: string | null;
  durationMin: number;
  note: string;
  /** Editing an existing entry (its client_id) rather than adding one. */
  isEdit: boolean;
  /** `failed` = a send did not reach the server; the form shows the retry state. */
  status: 'editing' | 'failed';
}

const STORAGE_PREFIX = 'hofsync.labour.draft.v1:';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function draftStorageKey(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`;
}

/** A v4 UUID (D8: `client_id` must be a UUID generated on the phone). */
export function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function newDraft(workDay: string): LabourDraft {
  return {
    clientId: newClientId(),
    workDay,
    step: 0,
    activityKey: null,
    costObjectKey: null,
    durationMin: DEFAULT_DURATION,
    note: '',
    isEdit: false,
    status: 'editing',
  };
}

export function clampDuration(minutes: number): number {
  if (!Number.isFinite(minutes)) return DURATION_MIN;
  const stepped = Math.round(minutes / DURATION_STEP) * DURATION_STEP;
  return Math.min(DURATION_MAX, Math.max(DURATION_MIN, stepped));
}

function isDraft(value: unknown): value is LabourDraft {
  const d = value as Partial<LabourDraft> | null;
  return (
    !!d &&
    typeof d.clientId === 'string' &&
    UUID_RE.test(d.clientId) &&
    typeof d.workDay === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(d.workDay) &&
    (d.step === 0 || d.step === 1 || d.step === 2) &&
    typeof d.durationMin === 'number' &&
    typeof d.note === 'string'
  );
}

export function loadDraft(scope: string): LabourDraft | null {
  try {
    const raw = localStorage.getItem(draftStorageKey(scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isDraft(parsed)) return null;
    return {
      ...parsed,
      activityKey: parsed.activityKey ?? null,
      costObjectKey: parsed.costObjectKey ?? null,
      isEdit: Boolean(parsed.isEdit),
      status: parsed.status === 'failed' ? 'failed' : 'editing',
    };
  } catch {
    return null;
  }
}

export function saveDraft(scope: string, draft: LabourDraft): void {
  try {
    localStorage.setItem(draftStorageKey(scope), JSON.stringify(draft));
  } catch {
    // No storage: the draft lives only as long as the page.
  }
}

export function clearDraft(scope: string): void {
  try {
    localStorage.removeItem(draftStorageKey(scope));
  } catch {
    // Nothing to clear.
  }
}
